import { useContext } from "react";
import { Box, IconButton, Switch, Tooltip, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { FormEvent, useEffect, useRef, useState } from "react";
import { RiMailForbidLine } from "react-icons/ri";
import { TbSend } from "react-icons/tb";

import { StyledButton } from "../../components";
import StyledInput from "../../components/StyledInput";
import { pink, purple } from "../../config";
import { WebsocketContext } from "../../contexts/websocketContext";
import useCurrentSlideData from "../../hooks/useCurrentSlideData";
import useSocket from "../../hooks/useWebsocket";
import { Comment } from "../../interfaces/answerStats.interface";
import useTranslatePageText from "@natklok/hooks/useTranslatePageText";

const SPAM_COOLDOWN_TIME: number = 1000 * 3;
let lastActionTime: Date = new Date();

const Comments: React.FC<{ readonly?: boolean }> = ({ readonly = false }) => {
  const commentContainerEl = useRef<HTMLDivElement>(null);
  const [comment, setComment] = useState("");
  const currentSlideData = useCurrentSlideData();
  const { wsSendComment, wsToggleBanPlayer } = useSocket();
  const { isMultiplayerHost, allowChat, handleSetAllowChat } = useContext(WebsocketContext);
  const translatePageText = useTranslatePageText();

  useEffect(() => {
    // So people can write comments straight away
    lastActionTime.setUTCMilliseconds(new Date().getUTCMilliseconds() - SPAM_COOLDOWN_TIME);
  }, []);

  const handleSendComment = () => {
    const now: Date = new Date();
    const timeElapsed = now.getTime() - lastActionTime.getTime();
    if (timeElapsed > SPAM_COOLDOWN_TIME) {
      if (comment !== "") {
        lastActionTime = new Date();
        wsSendComment(comment);
        setComment("");
      }
    }
  };

  const handleBanPlayer = (playerId: string, name: string) => {
    const ban = window.confirm(
      `Är du säker att du vill förbjuda '${name}' från att kommentera? Detta går inte att återställa.`
    );
    if (ban) {
      wsToggleBanPlayer(playerId, true);
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSendComment();
  };

  useEffect(() => {
    if (commentContainerEl.current !== null) {
      commentContainerEl.current.addEventListener("DOMNodeInserted", (event) => {
        const { currentTarget: target } = event;
        if (target instanceof HTMLDivElement) {
          target.scroll({ top: target.scrollHeight, behavior: "smooth" });
        }
      });
    }
  }, []);

  return (
    <Box
      marginY={2}
      width="100%"
      height={readonly ? "50vh" : 340}
      borderRadius={2}
      bgcolor={purple[400]}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      <Box
        padding={0.5}
        textAlign="center"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        paddingX="16px"
      >
        <Typography variant="h6" color={grey[100]}>
          {translatePageText("forTeachers.questionsAndComments")}
        </Typography>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            background: "rgba(255,255,255,0.2)",
            padding: "0 6px",
            borderRadius: 8,
          }}
        >
          <Typography style={{ fontSize: 16, opacity: 0.8 }}>
            {translatePageText("general.off")}
          </Typography>
          <Switch
            defaultChecked={allowChat}
            value={allowChat}
            onChange={(e) => handleSetAllowChat(e.target.checked)}
            color="secondary"
          />
          <Typography style={{ fontSize: 16, opacity: 0.8 }}>
            {translatePageText("general.on")}
          </Typography>
        </div>
      </Box>
      <div
        ref={commentContainerEl}
        style={{
          overflowY: "scroll",
          background: purple[600],
          height: "100%",
          padding: 8,
          borderBottomLeftRadius: readonly ? 8 : 0,
          borderBottomRightRadius: readonly ? 8 : 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
        }}
      >
        {!allowChat && (
          <Box
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Typography style={{ fontSize: 16, opacity: 0.5 }}>
              {translatePageText("joinInformation.chatNotAvailable")}
            </Typography>
          </Box>
        )}
        {allowChat &&
          currentSlideData.answerStat?.comments.map((commentData: Comment) => (
            <Box
              bgcolor={purple[100]}
              color="black"
              marginY={0.5}
              display="flex"
              alignItems="center"
              textAlign="start"
              borderRadius={2}
              padding={0.5}
              key={commentData.text}
            >
              {isMultiplayerHost && (
                <Tooltip
                  title="Förbjud kommentarer från denna spelare"
                  style={{ background: purple[300] }}
                >
                  <IconButton
                    size="small"
                    style={{ padding: 10, marginRight: 8, background: pink[500] }}
                    onClick={() => handleBanPlayer(commentData.id, commentData.from)}
                  >
                    <RiMailForbidLine />
                  </IconButton>
                </Tooltip>
              )}
              <b style={{ marginRight: 8 }}>{commentData.from}:</b>{" "}
              <p
                style={{
                  margin: "0 8px 0 0",
                  wordWrap: "break-word",
                  wordBreak: "break-all",
                }}
              >
                {commentData.text}
              </p>
            </Box>
          ))}
      </div>

      {readonly === false && (
        <Box
          padding={1}
          marginBottom={1}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            height: 36,
            marginTop: 8,
          }}
        >
          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            <StyledInput
              id="comment"
              value={comment}
              placeholder="Kommentera något"
              maxLength={150}
              style={{
                background: purple[100],
                height: 36,
                width: "100%",
                textAlign: "left",
              }}
              onChange={(e: any) => setComment(e.target.value)}
            />
          </form>
          <StyledButton
            variant="secondary"
            style={{ margin: "0 0 0 6px" }}
            size="small"
            iconPlacement="right"
            onClick={handleSendComment}
          >
            <TbSend />
          </StyledButton>
        </Box>
      )}
    </Box>
  );
};

export default Comments;
